/**
 * ThreadComposer — the thread's reply box, laid out as Figma 505:50225:
 * Head (Email channel + "To") → Editor → pill row ("Macros") → Footer
 * (formatting, hint, Save draft, Send). No subject: replies keep the thread's.
 *
 * It is always a reply on the thread's own subject. A counter only comes from
 * the Counter pill (our amounts, then the agent's counter draft); the final
 * confirmation is V2, so nothing here sends kind confirm. While Go's follow-up
 * signal is due (owner 2026-10-12: the creator's date has come, or 48h without
 * a reply), a Follow up pill offers an agent-written follow-up. Nothing is
 * sent without the human clicking Send; Save draft stores the text without
 * sending.
 */
import { useEffect, useRef, useState } from 'react';
import { useOutreachComposerDraft, useOutreachNegotiation } from '@discovr/features';
import type { OutreachComposerDraft } from '@discovr/features';
import type { OutreachMessage, OutreachQuote, OutreachRate, OutreachReplyChip, OutreachRow, OutreachSendKind } from '@discovr/types';
import { fmtDeliverableType, isOutreachProgress } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { fmtRate } from './QuoteBlock';
import { CounterRatesDialog } from './CounterRatesDialog';
import { codeOf, counterDraftErrorCopy, detailsOf, errorCopy, helpMeWriteErrorCopy, helpMeWriteRetryable } from './sendLabels';
import { AiFailureNotice } from './AiFailureNotice';
import { NOT_WORKING_COPY, aiFailureOf, toastAiFailure, type AiFailure } from './aiFailure';
import { canCounter, hasMailedQuote, needsRates } from './composerKinds';
import { hasRateToHandOff, isInOutreach } from './handoffStates';
import { ReplySuggestionStrip, type ComposerPill } from './ReplySuggestionStrip';
import { PARSE_PENDING_MS, replyChipsFor } from './replyChips';
import { ComposerEditor, type ComposerEditorHandle } from './composerEditor/ComposerEditor';
import { canonicalHtml, hasFormatting } from './composerEditor/stripHtmlAttributes';
import { HelpMeWritePanel } from './HelpMeWritePanel';
import { OUTREACH_HELP_ME_WRITE_ENABLED } from './outreachFlags';
import { REPLY_BLOCKER_COPY, replyBlocker } from './composerGate';
import { escalationLabel, followupLabel, isFollowupDue } from './rowStateLabels';
import { toastSendStatus } from './sendStatusToast';
import gmailLogo from '../../../assets/connectors/gmail.png';
import { PaperPlaneTilt, Sparkle } from '../../../design-system/icons';

/** What this composer sends: the final confirmation (kind confirm) is V2 and has no path here. */
export type ComposerKind = Extract<OutreachSendKind, 'reply' | 'counter'>;

/** followup: the Template text goes out as the due Follow up. */
type ComposerAiError = { failure: AiFailure; label: string; retry: () => void; followup: boolean };

export interface ComposerDraft {
  kind: ComposerKind;
  body: string;
  bodyHtml?: string;
  rates?: OutreachRate[];
  suggestionJobId?: string;
}

export interface ThreadComposerProps {
  campaignId: string;
  entryId: string;
  row: OutreachRow;
  canWrite: boolean;
  latestQuote?: OutreachQuote;
  /** The thread's rows: the suggestions ride on the latest inbound one. */
  messages: OutreachMessage[];
  /** Help me write is V2: kept behind its build flag, off by default. */
  helpMeWriteEnabled?: boolean;
  /** The Confirm rate pill opens the Agree rate dialog through this; absent where agreeing is not allowed. */
  onAgreeRate?: () => void;
  /** Why Confirm rate cannot be used yet (an amount on the latest quote is still unmatched). */
  agreeBlockedReason?: string;
  /** A thread with no address offers Add email through this (opens the creator's contact details). */
  onAddEmail?: () => void;
  /** A prepared draft (final confirmation, acknowledgement) — fills the composer, never sends on its own. */
  initialDraft?: ComposerDraft;
  onSent?: () => void;
  /** The campaign's send zone: the follow-up countdown counts calendar days in it. */
  campaignZone?: string;
}

/** followup: the text came from the due Follow up pill (Go #706); editing keeps it, clearing it or another suggestion drops it. */
type DraftSnapshot = { kind: ComposerKind; body: string; bodyHtml: string; rates: OutreachRate[]; suggestionJobId: string | null; followup: boolean; templateStep: number | null };

function failedStepFailure(row: OutreachRow): AiFailure | null {
  const f = row.followup;
  if (!f || f.kind !== 'steps_remaining' || f.state !== 'failed' || row.followupsPausedAt || !isInOutreach(row)) return null;
  return {
    message: f.step ? `Follow-up step ${f.step} failed.` : 'The automatic follow-up failed.',
    reason: f.reason ?? '',
    retryable: f.retryable !== false,
    ...(f.templateStep ? { templateStep: f.templateStep } : {}),
  };
}

const FOLLOWUP_PILL = 'followup';
const FOLLOWUP_WAIT_PILL = 'followup-wait';
const COUNTER_PILL = 'counter';

/** Mirrors Go's projection.NoReplyFollowupAfter: a no-reply follow-up falls due 48h after the email or dateless hold it waits on. */
const NO_REPLY_FOLLOWUP_AFTER_MS = 48 * 60 * 60 * 1000;

/** A real reply newer than the silence a no-reply follow-up counts from: the thread is ahead of the row. */
function repliedSince(messages: readonly OutreachMessage[], sinceMs: number): boolean {
  return messages.some((m) => m.direction === 'in' && !m.isAutoReply && !m.isDsn && Date.parse(m.receivedAt ?? m.createdAt) > sinceMs);
}

/** A message to the creator still queued or sending is the follow-up: none is offered until it is sent (owner 2026-10-12). */
const outboundPending = (messages: readonly OutreachMessage[]) =>
  messages.some((m) => m.direction === 'out' && (m.status === 'queued' || m.status === 'sending'));

/** E9-164: the thread holds outbound mail but none of it was ever sent (a held or unsent first touch), so there is nothing to follow up. */
const nothingSent = (messages: readonly OutreachMessage[]) => {
  const out = messages.filter((m) => m.direction === 'out');
  return out.length > 0 && !out.some((m) => m.status === 'sent');
};

/** The Follow up pill: offered while Go's follow-up signal is due and outreach still holds the row. */
function showFollowupPill(row: OutreachRow, messages: readonly OutreachMessage[], now: Date): boolean {
  const f = row.followup;
  if (!f || f.kind === 'steps_remaining' || !isInOutreach(row) || !isFollowupDue(f, now) || outboundPending(messages) || nothingSent(messages)) return false;
  return f.kind !== 'no_reply' || !repliedSince(messages, Date.parse(f.dueAt) - NO_REPLY_FOLLOWUP_AFTER_MS);
}

/** Before a creator's date the pill counts down ("Follow up in N days", owner 2026-10-12) and cannot be used. */
function followupCountdown(row: OutreachRow, messages: readonly OutreachMessage[], now: Date): string | null {
  const f = row.followup;
  if (!f || f.kind !== 'creator_wait' || !isInOutreach(row) || isFollowupDue(f, now) || outboundPending(messages)) return null;
  return followupLabel(row, now)?.text ?? null;
}

const sameRates = (a: OutreachRate[], b: OutreachRate[]) =>
  a.length === b.length && a.every((r, i) => r.deliverable === b[i]?.deliverable && r.amount === b[i]?.amount && r.currency === b[i]?.currency);

const composerKindOf = (kind: OutreachComposerDraft['kind']): ComposerKind => (kind === 'counter' ? 'counter' : 'reply');

// A draft saved before formatting was kept has no HTML: it matches while the editor adds none.
const sameFormatting = (saved: string | undefined, current: string) =>
  saved === undefined ? !hasFormatting(current) : canonicalHtml(saved) === canonicalHtml(current);

function matchesSaved(saved: OutreachComposerDraft, d: { kind: ComposerKind; body: string; bodyHtml: string; rates: OutreachRate[] }) {
  return (
    composerKindOf(saved.kind) === d.kind &&
    saved.body === d.body &&
    sameFormatting(saved.bodyHtml, d.bodyHtml) &&
    sameRates(saved.rates, needsRates(d.kind) ? d.rates : [])
  );
}

export function ThreadComposer({
  campaignId,
  entryId,
  row,
  canWrite,
  latestQuote,
  messages,
  helpMeWriteEnabled = OUTREACH_HELP_ME_WRITE_ENABLED,
  onAgreeRate,
  agreeBlockedReason,
  onAddEmail,
  initialDraft,
  onSent,
  campaignZone,
}: ThreadComposerProps) {
  // Same override-over-profile precedence ContactCell uses.
  const toAddress = row.emailOverride ?? row.email;
  const blocker = replyBlocker(row, messages);
  const neg = useOutreachNegotiation(campaignId, entryId);
  const composerDraft = useOutreachComposerDraft(entryId, canWrite && !blocker);
  const [kind, setKind] = useState<ComposerKind>(initialDraft?.kind ?? 'reply');
  const [body, setBody] = useState(initialDraft?.body ?? '');
  const [bodyHtml, setBodyHtml] = useState(initialDraft?.bodyHtml ?? '');
  const [rates, setRates] = useState<OutreachRate[]>(initialDraft?.rates ?? []);
  const [counterOpen, setCounterOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiError, setAiError] = useState<ComposerAiError | null>(null);
  const [suggestionJobId, setSuggestionJobId] = useState<string | null>(initialDraft?.suggestionJobId ?? null);
  const [followup, setFollowup] = useState(false);
  // The sequence step the box text started as (Template button); null for anything else.
  const [templateStep, setTemplateStep] = useState<number | null>(null);
  const [quietNote, setQuietNote] = useState<string | null>(null);
  // messages.length when the "not working" line was put away (Retry found nothing owed, or filled the box): it stays gone until the thread changes.
  const [suggestionsHiddenAt, setSuggestionsHiddenAt] = useState<number | null>(null);
  const [seenMessageCount, setSeenMessageCount] = useState(messages.length);
  if (messages.length !== seenMessageCount) {
    setSeenMessageCount(messages.length);
    setAiError(null);
  }
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpError, setHelpError] = useState<string | null>(null);
  const [helpRetryable, setHelpRetryable] = useState(true);
  const draftRequest = useRef(0);
  const helpAbort = useRef<AbortController | null>(null);
  useEffect(() => () => helpAbort.current?.abort(), []);
  // Async drafts (counter) and sends read the box as it stands when they land, not as it stood at click time.
  const draftRef = useRef<DraftSnapshot>({ kind, body, bodyHtml, rates, suggestionJobId, followup, templateStep });
  useEffect(() => {
    draftRef.current = { kind, body, bodyHtml, rates, suggestionJobId, followup, templateStep };
  }, [kind, body, bodyHtml, rates, suggestionJobId, followup, templateStep]);
  // The pending skeleton only ends when something re-renders after the window; schedule that.
  const [, setClock] = useState(0);
  const pendingNow = replyChipsFor(messages).status === 'pending';
  useEffect(() => {
    if (!pendingNow) return;
    const timer = setTimeout(() => setClock((n) => n + 1), PARSE_PENDING_MS + 1000);
    return () => clearTimeout(timer);
  }, [pendingNow]);
  const editorHandle = useRef<ComposerEditorHandle>(null);
  // Lexical reads its seed once per mount: bumping the key remounts it with editorSeed as the new content.
  const [editorKey, setEditorKey] = useState(0);
  const [editorSeed, setEditorSeed] = useState({ text: initialDraft?.body ?? '', html: initialDraft?.bodyHtml ?? '' });

  const load = (d: ComposerDraft | OutreachComposerDraft) => {
    const html = 'bodyHtml' in d ? (d.bodyHtml ?? '') : '';
    setKind(composerKindOf(d.kind));
    setBody(d.body);
    setBodyHtml(html);
    setEditorSeed({ text: d.body, html });
    setEditorKey((k) => k + 1);
    setRates(d.kind === 'counter' ? (d.rates ?? []) : []);
    setSuggestionJobId(d.suggestionJobId ?? null);
    setFollowup(false);
    setTemplateStep(null);
    setError(null);
    setAiError(null);
  };

  // Undo goes back to the snapshot, text and formatting, by remounting the editor.
  const restoreDraft = (snap: DraftSnapshot) => {
    setKind(snap.kind);
    setBody(snap.body);
    setBodyHtml(snap.bodyHtml);
    setEditorSeed({ text: snap.body, html: snap.bodyHtml });
    setEditorKey((k) => k + 1);
    setRates(snap.rates);
    setSuggestionJobId(snap.suggestionJobId);
    setFollowup(snap.followup);
    setTemplateStep(snap.templateStep);
  };

  // A fresh seed (a new object identity each time) replaces whatever the composer holds, adjusted during
  // render so it lands in the same commit as the click that sent it; typed text stays recoverable via Undo.
  const [prevInitialDraft, setPrevInitialDraft] = useState(initialDraft);
  const [seedUndo, setSeedUndo] = useState<{ snap: DraftSnapshot; cleared: boolean } | null>(null);
  if (initialDraft !== prevInitialDraft) {
    setPrevInitialDraft(initialDraft);
    const clearing = !!initialDraft && initialDraft.body.trim() === '';
    // Clearing an already empty box changes nothing, so a counter's amounts stay put.
    if (initialDraft && !(clearing && body.trim() === '')) {
      if (body.trim() !== '') setSeedUndo({ snap: { kind, body, bodyHtml, rates, suggestionJobId, followup, templateStep }, cleared: clearing });
      load(initialDraft);
    }
  }
  // Each replaced snapshot is a new object, so the toast fires once per seed. An empty seed clears the box.
  useEffect(() => {
    if (!seedUndo) return;
    toast.message(seedUndo.cleared ? 'Cleared your draft' : 'Replaced your draft', { action: { label: 'Undo', onClick: () => restoreDraft(seedUndo.snap) } });
  }, [seedUndo]);

  // Focus returns to the message box once the counter text lands (it was hidden behind the skeleton).
  const writing = neg.counterDraft.isPending || neg.followupDraft.isPending || neg.nudgeDraft.isPending || neg.replySuggestion.isPending;
  const [wasWriting, setWasWriting] = useState(writing);
  const [writesDone, setWritesDone] = useState(0);
  if (writing !== wasWriting) {
    setWasWriting(writing);
    if (!writing) setWritesDone((n) => n + 1);
  }
  useEffect(() => {
    if (writesDone > 0) editorHandle.current?.focus();
  }, [writesDone]);

  // Reopening a thread restores its saved draft once (from the cache too), unless a seed or typing got there first.
  const saved = composerDraft.draft.data ?? null;
  const [restoreChecked, setRestoreChecked] = useState(false);
  if (!restoreChecked && (composerDraft.draft.isSuccess || composerDraft.draft.isError)) {
    setRestoreChecked(true);
    if (saved && !initialDraft && body.trim() === '') load(saved);
  }

  if (!canWrite) {
    return <p className="border-t px-4 py-3 text-sm text-muted-foreground">You can read this thread but not reply.</p>;
  }

  if (blocker) {
    // 'closed_removed' skips role="status": ThreadView already renders its own banner for a removed creator.
    const offerAdd = blocker === 'no_contact' && onAddEmail;
    return (
      <div className="flex items-center justify-between gap-3 border-t px-4 py-3">
        <p role={blocker === 'closed_removed' ? undefined : 'status'} className="text-sm text-muted-foreground">
          {REPLY_BLOCKER_COPY[blocker]}
        </p>
        {offerAdd && (
          <Button type="button" size="sm" variant="outline" onClick={onAddEmail}>
            Add email
          </Button>
        )}
      </div>
    );
  }

  const busy = neg.send.isPending || writing || neg.templateStep.isPending || neg.helpMeWrite.isPending;
  const hasBody = body.trim() !== '';
  // A no-contact thread gates Send too, rather than failing opaquely at the BE.
  const saving = composerDraft.save.isPending || composerDraft.discard.isPending;
  // A save still in flight could land after the send cleared the draft and bring it back, so Send waits for it.
  const canSend = !!toAddress && hasBody && !neg.send.isPending && !writing && !neg.templateStep.isPending && !saving;

  const showError = (e: unknown) => setError(errorCopy(codeOf(e), detailsOf(e)));

  const failAi = (e: unknown, label: string, retry: () => void, followupText = false): boolean => {
    const failure = aiFailureOf(e);
    if (!failure) return false;
    setAiError({ failure, label, retry, followup: followupText });
    toastAiFailure(failure);
    return true;
  };

  // A suggestion overwrites the composer at once (no confirm dialog); whatever was typed stays recoverable through Undo.
  const applySuggestion = (
    next: { kind: ComposerKind; body: string; bodyHtml?: string; rates: OutreachRate[]; suggestionJobId: string | null; followup?: boolean; templateStep?: number },
    undoMessage = 'Replaced your draft',
  ) => {
    const previous = draftRef.current.body.trim() !== '' ? draftRef.current : null;
    if (!editorHandle.current?.replaceWithContent(next.body, next.bodyHtml)) return;
    setKind(next.kind);
    setFollowup(next.followup ?? false);
    setTemplateStep(next.templateStep ?? null);
    setQuietNote(null);
    setRates(next.rates);
    editorHandle.current?.focus();
    setSuggestionJobId(next.suggestionJobId);
    if (previous) {
      toast.message(undoMessage, { action: { label: 'Undo', onClick: () => restoreDraft(previous) } });
    }
  };

  const openCounter = () => {
    setCounterOpen(true);
  };

  // A reply suggestion fills the whole mail as a plain reply; the human edits and sends.
  const fillReply = (chip: OutreachReplyChip) => {
    draftRequest.current += 1;
    setError(null);
    setAiError(null);
    applySuggestion({ kind: 'reply', rates: [], body: chip.draft.body, suggestionJobId: null });
  };

  const draftCounter = async (r: OutreachRate[]) => {
    setCounterOpen(false);
    setError(null);
    setAiError(null);
    const request = ++draftRequest.current;
    try {
      const d = await neg.counterDraft.mutateAsync(r);
      if (request !== draftRequest.current) return;
      applySuggestion({ kind: 'counter', rates: r, body: d.body, bodyHtml: d.bodyHtml, suggestionJobId: d.jobId ?? null });
    } catch (e) {
      if (request !== draftRequest.current) {
        const stale = aiFailureOf(e);
        if (stale) toastAiFailure(stale);
        else toast.error(counterDraftErrorCopy(codeOf(e), detailsOf(e)));
        return;
      }
      applySuggestion({ kind: 'reply', rates: [], body: '', suggestionJobId: null }, 'Cleared your draft');
      if (!failAi(e, 'counter', () => void draftCounter(r))) setError(counterDraftErrorCopy(codeOf(e), detailsOf(e)));
    }
  };

  // The Follow up pill: an agent-written follow-up fills the box as a reply; nothing is sent until the human clicks Send.
  const requestFollowup = async () => {
    setError(null);
    setAiError(null);
    setQuietNote(null);
    const request = ++draftRequest.current;
    try {
      const d = await neg.followupDraft.mutateAsync();
      if (request !== draftRequest.current) return;
      applySuggestion({ kind: 'reply', rates: [], body: d.body, bodyHtml: d.bodyHtml, suggestionJobId: d.jobId ?? null, followup: true });
    } catch (e) {
      if (request !== draftRequest.current) return;
      if (codeOf(e) === 'NO_FOLLOWUP_DUE') setQuietNote(errorCopy('NO_FOLLOWUP_DUE'));
      else if (!failAi(e, 'follow-up', () => void requestFollowup(), true)) showError(e);
    }
  };

  const retryNudge = async () => {
    setError(null);
    setAiError(null);
    const request = ++draftRequest.current;
    try {
      const d = await neg.nudgeDraft.mutateAsync();
      if (request !== draftRequest.current) return;
      applySuggestion({ kind: 'reply', rates: [], body: d.body, bodyHtml: d.bodyHtml, suggestionJobId: d.jobId ?? null });
    } catch (e) {
      if (request !== draftRequest.current) return;
      if (!failAi(e, 'follow-up', () => void retryNudge())) showError(e);
    }
  };

  // Sent with fromTemplateStep, so the text is stamped as template, never as the AI's.
  const insertTemplate = async (step: number, followupText: boolean) => {
    setError(null);
    const request = ++draftRequest.current;
    try {
      const t = await neg.templateStep.mutateAsync(step);
      if (request !== draftRequest.current) return;
      setAiError(null);
      applySuggestion({ kind: 'reply', rates: [], body: t.body, bodyHtml: t.bodyHtml, suggestionJobId: null, followup: followupText, templateStep: step });
    } catch (e) {
      if (request === draftRequest.current) showError(e);
    }
  };

  const retryReplySuggestion = async () => {
    setError(null);
    setAiError(null);
    const request = ++draftRequest.current;
    try {
      const d = await neg.replySuggestion.mutateAsync();
      if (request !== draftRequest.current) return;
      applySuggestion({ kind: 'reply', rates: [], body: d.body, bodyHtml: d.bodyHtml, suggestionJobId: d.jobId ?? null });
      setSuggestionsHiddenAt(messages.length);
    } catch (e) {
      if (request !== draftRequest.current) return;
      if (codeOf(e) === 'NO_SUGGESTION') {
        const reason = detailsOf(e)?.reason;
        if (reason === 'no_inbound' || reason === 'answered_since') setSuggestionsHiddenAt(messages.length);
        else toastAiFailure({ message: NOT_WORKING_COPY, reason: typeof reason === 'string' ? reason : '', retryable: true });
        return;
      }
      if (!failAi(e, 'reply suggestions', () => void retryReplySuggestion())) showError(e);
    }
  };

  const stopHelpMeWrite = () => helpAbort.current?.abort();
  const helpMeWrite = async (prompt: string) => {
    setHelpError(null);
    const request = ++draftRequest.current;
    const controller = new AbortController();
    helpAbort.current = controller;
    const previous = { body, html: bodyHtml || undefined };
    let streamed = '';
    const discard = () => {
      if (streamed) editorHandle.current?.replaceWithContent(previous.body, previous.html);
    };
    try {
      const s = await neg.helpMeWrite.mutateAsync({
        prompt,
        kind,
        rates: needsRates(kind) ? rates : undefined,
        signal: controller.signal,
        onToken: (t) => {
          if (request !== draftRequest.current || controller.signal.aborted) return;
          streamed += t;
          editorHandle.current?.replaceWithPlainText(streamed);
        },
      });
      if (request !== draftRequest.current) return;
      if (controller.signal.aborted || !editorHandle.current?.replaceWithContent(s.body, s.bodyHtml)) return discard();
      editorHandle.current.focus();
      setSuggestionJobId(s.jobId ?? null);
      setFollowup(false);
      setTemplateStep(null);
      setHelpOpen(false);
    } catch (e) {
      if (request !== draftRequest.current) return;
      discard();
      if (!controller.signal.aborted) {
        setHelpError(helpMeWriteErrorCopy(e));
        setHelpRetryable(helpMeWriteRetryable(e));
      }
    } finally {
      if (helpAbort.current === controller) helpAbort.current = null;
    }
  };

  const stepFailure = failedStepFailure(row);
  const aiTemplate = aiError?.failure.templateStep;
  const stepTemplate = stepFailure?.templateStep;
  const chipsOpen = !hasRateToHandOff(row) && !(isInOutreach(row) && !isOutreachProgress(row.outreachState));
  const counterAllowed = canCounter(row, hasMailedQuote(row, latestQuote));
  const chips = chipsOpen ? replyChipsFor(messages, undefined, undefined, row.escalatedAt) : ({ status: 'none' } as const);
  const suggestions = chips.status === 'ready' ? chips.chips : [];
  const CONFIRM_RATE = 'confirm-rate';
  const waitText = followupCountdown(row, messages, new Date());
  const pills: ComposerPill[] = [
    ...(onAgreeRate ? [{ id: CONFIRM_RATE, label: hasRateToHandOff(row) ? 'Revise rate' : 'Confirm rate', blockedReason: agreeBlockedReason }] : []),
    ...(showFollowupPill(row, messages, new Date()) ? [{ id: FOLLOWUP_PILL, label: 'Follow up' }] : []),
    ...(waitText ? [{ id: FOLLOWUP_WAIT_PILL, label: waitText, blockedReason: 'The creator asked us to wait. The follow-up unlocks on their date.' }] : []),
    ...suggestions.map((chip, i) => ({ id: String(i), label: chip.label })),
    ...(chipsOpen && counterAllowed ? [{ id: COUNTER_PILL, label: 'Counter' }] : []),
  ];
  const selectPill = (id: string) => {
    if (id === CONFIRM_RATE) {
      onAgreeRate?.();
      return;
    }
    if (id === FOLLOWUP_PILL) {
      void requestFollowup();
      return;
    }
    if (id === COUNTER_PILL) {
      openCounter();
      return;
    }
    const chip = suggestions[Number(id)];
    if (chip) fillReply(chip);
  };

  const isSaved = !!saved && matchesSaved(saved, { kind, body, bodyHtml, rates });
  const discardMode = !hasBody && !!saved;
  const saveLabel = saving ? 'Saving…' : discardMode ? 'Discard draft' : isSaved ? 'Saved' : 'Save draft';
  const canSave = !saving && !busy && (discardMode || (hasBody && !isSaved));

  const saveDraft = () => {
    if (!canSave) return;
    setError(null);
    if (discardMode) {
      composerDraft.discard.mutate(undefined, {
        onSuccess: () => toast.success('Draft discarded'),
        onError: (e) => setError(errorCopy(codeOf(e), detailsOf(e))),
      });
      return;
    }
    composerDraft.save.mutate(
      { kind, body, bodyHtml: bodyHtml || undefined, rates: needsRates(kind) ? rates : [], suggestionJobId: suggestionJobId ?? undefined },
      {
        onSuccess: () => toast.success('Draft saved'),
        onError: (e) => setError(errorCopy(codeOf(e), detailsOf(e), 'save')),
      },
    );
  };

  const send = () => {
    if (!canSend) return;
    setError(null);
    setQuietNote(null);
    const sentBody = body;
    neg.send.mutate(
      // V2: negotiation history — a counter would go as kind=counter with its rates. In V1 it is just an email.
      {
        kind: 'reply',
        body,
        bodyHtml: bodyHtml || undefined,
        suggestionJobId: suggestionJobId ?? undefined,
        clearComposerDraft: true,
        ...(followup ? { followup: true } : {}),
        ...(templateStep !== null ? { fromTemplateStep: templateStep } : {}),
      },
      {
        onSuccess: (result) => {
          // Text typed while the send was in flight is kept; it starts over as a plain reply.
          if (draftRef.current.body === sentBody) load({ kind: 'reply', body: '' });
          else {
            setKind('reply');
            setRates([]);
            setSuggestionJobId(null);
            setFollowup(false);
            setTemplateStep(null);
          }
          toastSendStatus('Reply', result);
          onSent?.();
        },
        onError: showError,
      },
    );
  };

  return (
    <div
      className="mx-auto w-full max-w-[832px] overflow-hidden rounded-xl border border-border bg-card shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_6px_-1px_rgba(0,0,0,0.04)]"
      data-testid="thread-composer"
    >
      {/* Email only for V1 (ruling 37): Figma's two-level "Channel Tabs" pill keeps its shell around the one channel. */}
      {/* Figma's strokes sit inside its 195px frame: 7px padding here and 11px in the footer absorb this card's borders. The tab is 24px in a 32px pill. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-3 py-[7px]">
        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 p-1">
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-[3px] text-[12px]/4 font-medium text-foreground">
            <img src={gmailLogo} alt="" className="h-4 w-auto" />
            Email
          </span>
        </span>
        {toAddress && <span className="text-[12px] text-muted-foreground">{`To ${toAddress}`}</span>}
      </div>
      {helpMeWriteEnabled && helpOpen && (
        <HelpMeWritePanel
          kind={kind}
          ratesReady={rates.some((r) => r.amount > 0)}
          busy={neg.helpMeWrite.isPending}
          error={helpError}
          retryable={helpRetryable}
          onWrite={(p) => void helpMeWrite(p)}
          onStop={stopHelpMeWrite}
          onEditRates={() => setCounterOpen(true)}
          onClose={() => {
            stopHelpMeWrite();
            setHelpOpen(false);
            setHelpError(null);
          }}
        />
      )}
      {kind === 'counter' && rates.length > 0 && (
        <p data-testid="composer-counter" className="px-4 pt-3 text-[12px] text-muted-foreground">
          {`Counter: ${rates.map((r) => `${fmtDeliverableType(r.deliverable)} ${fmtRate(r.amount, r.currency)}`).join(' · ')}`}
        </p>
      )}
      <ComposerEditor
        key={editorKey}
        handleRef={editorHandle}
        initialText={editorSeed.text}
        initialHtml={editorSeed.html}
        placeholder="Write a reply…"
        onChange={(text, html) => {
          setBody(text);
          setBodyHtml(html);
          if (text.trim() === '') {
            setFollowup(false);
            setTemplateStep(null);
          }
        }}
        onSubmitShortcut={send}
        writingLabel={
          neg.counterDraft.isPending ? 'Writing the counter…' : neg.followupDraft.isPending || neg.nudgeDraft.isPending ? 'Writing the follow-up…' : neg.replySuggestion.isPending ? 'Writing the reply…' : undefined
        }
        belowEditor={
          <>
            <ReplySuggestionStrip pending={chips.status === 'pending'} pills={pills} disabled={busy} onSelect={selectPill} />
            {templateStep !== null && (
              <p data-testid="composer-template-note" className="px-4 pb-3 text-xs text-muted-foreground">
                From the template. The assistant did not write this.
              </p>
            )}
            {aiError ? (
              <AiFailureNotice
                className="px-4 pb-3"
                failure={aiError.failure}
                label={aiError.label}
                busy={busy}
                onRetry={aiError.retry}
                onTemplate={aiTemplate ? () => void insertTemplate(aiTemplate, aiError.followup) : undefined}
              />
            ) : stepFailure && !hasBody ? (
              <AiFailureNotice
                className="px-4 pb-3"
                failure={stepFailure}
                label="follow-up"
                busy={busy}
                onRetry={() => void retryNudge()}
                onTemplate={stepTemplate ? () => void insertTemplate(stepTemplate, false) : undefined}
              />
            ) : chips.status === 'escalated' && suggestionsHiddenAt !== messages.length ? (
              <p data-testid="reply-chips-escalated" role="status" className="px-4 pb-3 text-xs text-muted-foreground">
                {row.escalationReason ? `No suggested reply — needs you · ${escalationLabel(row.escalationReason)}` : 'No suggested reply — needs you'}
              </p>
            ) : chips.status === 'unavailable' && chips.reason === 'no_ai_chip' && suggestionsHiddenAt !== messages.length ? (
              <p data-testid="reply-chips-none" role="status" className="px-4 pb-3 text-xs text-muted-foreground">
                No suggested reply for this message.
              </p>
            ) : chips.status === 'unavailable' && suggestionsHiddenAt !== messages.length ? (
              <AiFailureNotice
                className="px-4 pb-3"
                failure={{ message: "Suggested replies aren't working for now.", reason: chips.reason, retryable: true }}
                label="reply suggestions"
                busy={busy}
                onRetry={() => void retryReplySuggestion()}
              />
            ) : null}
            {quietNote && (
              <p role="status" className="px-4 pb-3 text-xs text-muted-foreground">
                {quietNote}
              </p>
            )}
            {composerDraft.draft.isError && <p className="px-4 pb-3 text-xs text-muted-foreground">Couldn&apos;t load your saved draft.</p>}
            {error && (
              <p role="alert" className="px-4 pb-3 text-xs text-destructive">
                {error}
              </p>
            )}
          </>
        }
        renderFooter={(toolbar) => (
          // Figma's footer: #fafafa fill and a stone-200 top border.
          // globals.css has an unlayered `* { border-color: var(--border) }` that beats any layered utility, hence the `!`.
          <div className="flex flex-wrap items-center justify-between gap-1 border-t border-t-(--stone-200)! bg-(--background-primary-light) px-3 py-[11px]">
            <div className="flex min-w-0 flex-wrap items-center gap-1">
              {helpMeWriteEnabled && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Help me write"
                    aria-expanded={helpOpen}
                    title="Help me write"
                    disabled={neg.send.isPending}
                    onClick={() => setHelpOpen((open) => !open)}
                    className="rounded-lg text-(--brand-500)"
                  >
                    <Sparkle size={20} weight="fill" />
                  </Button>
                  <span aria-hidden data-testid="help-divider" className="h-[18px] w-px bg-(--stone-300)" />
                </>
              )}
              <div className="flex items-center gap-2">
                {toolbar}
                <span className="text-[12px] text-(--foreground-subtle)">⌘↵ to send</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button type="button" variant="ghost" size="sm" className="h-[30px] rounded-full px-4 text-(--foreground-secondary-default) disabled:opacity-[0.36]" onClick={saveDraft} disabled={!canSave}>
                {saveLabel}
              </Button>
              <Button
                type="button"
                size="sm"
                // Figma's 90px Send: 16px sides and an 8px gap; the small size's icon padding would shrink it to 75px.
                className="h-[30px] min-w-[90px] gap-2 rounded-full bg-(--foreground-ink) px-4 text-white hover:bg-(--foreground-ink)/90 has-[>svg]:px-4"
                onClick={send}
                disabled={!canSend}
                title={!toAddress ? 'No email on file' : undefined}
              >
                {neg.send.isPending ? 'Sending…' : 'Send'}
                <PaperPlaneTilt size={16} />
              </Button>
            </div>
          </div>
        )}
      />
      <CounterRatesDialog
        open={counterOpen}
        platform={row.platform}
        quote={latestQuote}
        quoteAsPlaceholder
        title="Your counter-offer"
        submitLabel="Counter"
        onSubmit={(r) => void draftCounter(r)}
        onCancel={() => {
          setCounterOpen(false);
        }}
      />
    </div>
  );
}
